import {
  collection as collectionMobile,
  FirebaseFirestoreTypes,
  onSnapshot as onSnapshotMobile,
  orderBy as orderByMobile,
  query as queryMobile
} from '@react-native-firebase/firestore';
import { router, useLocalSearchParams, useNavigation } from 'expo-router';
import { collection, DocumentData, Firestore, onSnapshot, orderBy, query, QuerySnapshot } from 'firebase/firestore';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import {
  useDeleteMsgIndividualNpo,
  useEditMsgIndividualNpo,
  usePublishMsgIndividualNpo
} from '@/api/hooks/chatRoomsHooks';
import { Chat, ChatRoom, DeleteMessageData, EditMessageData, NewMessageData } from '@/api/models/chatRoomModels';
import { bs_icon } from '@/assets/images';
import { profilePlaceholder } from '@/assets/images/icons';
import GoBackButton from '@/components/buttons/GoBackButton';
import DotsLoader from '@/components/DotsLoader';
import ChatListWithInput from '@/components/messages/ChatListWithInput';
import ChatRoomHeader from '@/components/messages/ChatRoomHeader';
import Text from '@/components/Text';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { db } from '@/firebaseConfig';
import { firebaseImage, safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';
import { AxiosError } from 'axios';

const UserNpoChat = () => {
  const { state } = useAuth();
  const { showError } = useEndFlowModal();

  const navigation = useNavigation();
  const { chatJson, individualThumbnail, npoThumbnail } = useLocalSearchParams<{
    chatJson: string;
    individualThumbnail: string;
    npoThumbnail: string;
  }>();
  const { state: auth } = useAuth();
  const chatRoom = safeJsonParse<ChatRoom>(chatJson);

  const { mutateAsync: publishMsg } = usePublishMsgIndividualNpo();
  const { mutateAsync: editMsg } = useEditMsgIndividualNpo();
  const { mutateAsync: deleteMsg } = useDeleteMsgIndividualNpo();

  const [chats, setChats] = useState<Chat[]>([]);
  const [isPending, setIsPending] = useState(true);

  useLayoutEffect(() => {
    if (chatRoom) {
      const thumbnail =
        chatRoom.thumbnail !== undefined
          ? firebaseImage(safeJsonParseArray(chatRoom.thumbnail ?? '[]').at(0))
          : auth.user?.user.role === 'INDIVIDUAL'
            ? (chatRoom.nonProfitThumb ?? bs_icon)
            : (chatRoom.individualThumb ?? profilePlaceholder);

      navigation.setOptions({
        headerBackVisible: false,
        headerLeft: () => <GoBackButton />,
        headerTitle: () => <ChatRoomHeader thumbnail={thumbnail} title={chatRoom.title} subtitle={chatRoom.subtitle} />
      });
    } else {
      showError({ title: 'No chat room' });
      router.back();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onResult = (
    collectionSnapshot:
      | FirebaseFirestoreTypes.QuerySnapshot<FirebaseFirestoreTypes.DocumentData>
      | QuerySnapshot<DocumentData>
  ) => {
    const changes = collectionSnapshot.docChanges();

    if (isPending) setIsPending(false);

    changes.forEach((chat) => {
      const sender =
        state.user?.user?.role === 'INDIVIDUAL'
          ? chat.doc.data().senderId === 1
            ? chatRoom?.sender
            : chatRoom?.receiver
          : chat.doc.data().senderId === 1
            ? chatRoom?.receiver
            : chatRoom?.sender;

      const senderThumb = chat.doc.data().senderId === 1 ? JSON.stringify([individualThumbnail]) : npoThumbnail;

      if (
        !chats.some((c) => c.id === chat.doc.id) &&
        chat.type === 'added' &&
        chat.newIndex > -1 &&
        chat.oldIndex === -1
      ) {
        chats.push({
          id: chat.doc.id,
          msg: chat.doc.data().msg,
          createdAt: chat.doc.data().createdAt,
          editAt: chat.doc.data().editAt,
          senderId: chat.doc.data().senderId,
          sender: sender,
          senderThumb: senderThumb
        });
        setChats([...chats]);
      }

      if (
        chats.some((c) => c.id === chat.doc.id) &&
        chat.type === 'removed' &&
        chat.newIndex === -1 &&
        chat.oldIndex > -1
      ) {
        const indexToDelete = chats.findIndex((c) => c.id === chat.doc.id);
        chats.splice(indexToDelete, 1);
        setChats([...chats]);
      }

      if (
        chats.some((c) => c.id === chat.doc.id) &&
        chat.type === 'modified' &&
        chat.doc.data().msg &&
        chat.newIndex &&
        chat.oldIndex
      ) {
        const indexToUpdate = chats.findIndex((c) => c.id === chat.doc.id);
        const updatedChat = {
          id: chat.doc.id,
          msg: chat.doc.data().msg,
          createdAt: chat.doc.data().createdAt,
          editAt: chat.doc.data().editAt,
          senderId: chat.doc.data().senderId,
          sender: sender,
          senderThumb: senderThumb
        };
        chats.splice(indexToUpdate, 1, updatedChat);
        setChats([...chats]);
      }
    });
  };

  const onError = (error: Error) => {
    showError({ title: error.name, message: error.message });
    router.back();
  };

  useEffect(() => {
    if (Platform.OS === 'web') {
      //Web
      const chatsSubscriber = onSnapshot(
        query(collection(db() as Firestore, 'userNpo', chatRoom?.id ?? '', 'chats'), orderBy('createdAt', 'asc')),
        onResult,
        onError
      );

      //Stop listening to messages
      return () => chatsSubscriber();
    } else {
      //Mobile
      const chatsSubscriber = onSnapshotMobile(
        queryMobile(
          collectionMobile(db() as FirebaseFirestoreTypes.Module, 'userNpo', chatRoom?.id ?? '', 'chats'),
          orderByMobile('createdAt', 'asc')
        ),
        onResult,
        onError
      );

      //Stop listening to messages
      return () => chatsSubscriber();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMsgSend = async (data: NewMessageData) => {
    try {
      publishMsg(
        {
          message: data.newMessage,
          nonProfitOrgId: chatRoom?.nonProfitOrgId ?? '',
          chatKey: chatRoom?.id ?? ''
        },
        {
          onError: (e) => {
            console.error(e);
          }
        }
      );
    } catch (e) {
      console.error(e);
    }
  };

  const onMsgEdited = async (data: EditMessageData) => {
    try {
      editMsg(
        {
          msgId: data.idMessage,
          nonProfitOrgId: chatRoom?.nonProfitOrgId ?? '',
          message: data.message,
          chatKey: chatRoom?.id ?? ''
        },
        {
          onError: (e) => {
            console.error(e);
          }
        }
      );
    } catch (e) {
      console.error(e);
    }
  };

  const onMsgDeleted = async (data: DeleteMessageData) => {
    try {
      deleteMsg(
        {
          msgId: data.idMessage,
          nonProfitOrgId: chatRoom?.id ?? '',
          chatKey: chatRoom?.id ?? ''
        },
        {
          onError: (e) => {
            try {
              const axiosError = e as AxiosError;
              const improvedMessage =
                (axiosError.response?.data as any).message === 'user is not the owner of the msg'
                  ? i18n.t('mix.delete_msg_error')
                  : (axiosError.response?.data as any).message;
              showError({
                message: improvedMessage
              });
            } catch (e) {
              console.error(e);
            }
          }
        }
      );
    } catch (e) {
      console.error(e);
    }
  };

  if (!chatRoom || (state.user?.user?.role !== 'INDIVIDUAL' && state.user?.user?.role !== 'NON_PROFIT_ORG')) {
    router.back();
    return;
  }

  if (isPending) {
    return (
      <View style={style.container}>
        <DotsLoader />
      </View>
    );
  }

  if (!isPending && chats.length === 0) {
    return (
      <View style={style.container}>
        <Text>{i18n.t('messages.no_messages')}</Text>
      </View>
    );
  }

  return (
    <ChatListWithInput
      chatRoom={chatRoom}
      chats={chats.toReversed()}
      msgSend={onMsgSend}
      msgEdited={onMsgEdited}
      msgDeleted={onMsgDeleted}
    />
  );
};

const style = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  }
});

export default UserNpoChat;
